import { useState, useEffect } from 'react'
import type { WebSocketStatus } from '../hooks/useWebSocket'
import { ProjectSelector } from './ProjectSelector'
import sfLogo from '../assets/icons/sf_logo.png'
import { useTheme } from '../contexts/ThemeContext'
import sidewayLogo from '../assets/SidewayLogoOnly.png'

interface HeaderProps {
  projectName: string
  wsStatus: WebSocketStatus
  onSettingsClick?: () => void
  /** workflowName is the true project_name for Temporal lookups — pass
   * separately from projectName (the repo/display name) since they diverge
   * whenever a repo-name collision occurred. */
  onProjectChange?: (projectName: string, workflowName?: string) => void
  onNewProject?: () => void
  /** Stops the workflow it is given: the one the confirmation named. */
  onStopProject?: (workflowId: string) => void
  /** The workflow Stop would stop, `forge-<project_name>`. */
  stopWorkflowId?: string | null
  /** The project on screen, so the selector shows it when another browser
   * session switches the server's selected project. */
  currentProjectName?: string
  onResumeProject?: (intent?: string) => void
  projectListKey?: number
  getAuthHeaders?: () => Record<string, string>
  awaitingCount?: number
  onAwaitingClick?: () => void
}

/** The project a workflow id names (`forge-<project_name>`). */
function stopTargetName(workflowId: string): string {
  return workflowId.replace(/^forge-/, '')
}

export function Header({ projectName, wsStatus, onSettingsClick, onProjectChange, onNewProject, onStopProject, stopWorkflowId, currentProjectName, onResumeProject, projectListKey, getAuthHeaders, awaitingCount, onAwaitingClick }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  // The workflow the open stop confirmation names. Stop asks first and then
  // stops exactly this one: on 2026-10-12 a Stop meant for one project went
  // to another (L3-10).
  const [pendingStop, setPendingStop] = useState<string | null>(null)
  const [showResumeInput, setShowResumeInput] = useState(false)
  const [resumeMessage, setResumeMessage] = useState('')
  const [showAmendConfirm, setShowAmendConfirm] = useState(false)
  const { theme } = useTheme()
  const isCorporate = theme === 'corporate'

  const statusColors = {
    connected: 'bg-emerald-500',
    connecting: 'bg-amber-500',
    disconnected: 'bg-red-500',
    error: 'bg-red-500',
  }

  const statusLabels = {
    connected: 'Connected',
    connecting: 'Connecting',
    disconnected: 'Disconnected',
    error: 'Error',
  }

  // Plain resume — no intent, no brainstorm
  const handlePlainResume = () => {
    if (onResumeProject) {
      onResumeProject(undefined)
      setResumeMessage('')
      setShowResumeInput(false)
    }
  }

  // Resume with intent — requires confirmation since it triggers brainstorm/discuss
  const handleResumeWithIntent = () => {
    const intent = resumeMessage.trim()
    if (!intent) {
      handlePlainResume()
      return
    }
    setShowAmendConfirm(true)
  }

  const confirmAmendResume = () => {
    if (onResumeProject) {
      onResumeProject(resumeMessage.trim())
      setResumeMessage('')
      setShowResumeInput(false)
      setShowAmendConfirm(false)
    }
  }

  // A confirmation for a project that is no longer on screen is withdrawn.
  useEffect(() => {
    setPendingStop(null)
  }, [stopWorkflowId])

  const requestStop = () => {
    if (stopWorkflowId) setPendingStop(stopWorkflowId)
  }

  const confirmStop = () => {
    if (pendingStop && onStopProject) onStopProject(pendingStop)
    setPendingStop(null)
    setMobileMenuOpen(false)
  }

  const stopConfirmation = pendingStop && (
    <div data-testid="stop-confirm" className="flex items-center gap-2 bg-red-900/30 border border-red-600/50 rounded-lg px-3 py-2 max-lg:flex-col max-lg:items-stretch">
      <span className="text-sm text-red-200">
        Stop <span className="font-semibold">{stopTargetName(pendingStop)}</span>?
      </span>
      <div className="flex gap-2">
        <button
          onClick={confirmStop}
          className="px-3 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-500 rounded transition-colors"
        >
          Stop {stopTargetName(pendingStop)}
        </button>
        <button
          onClick={() => setPendingStop(null)}
          className="px-3 py-1 text-xs font-medium text-gray-300 hover:text-white transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  )

  const showAwaiting = awaitingCount != null && awaitingCount > 0 && onAwaitingClick
  const awaitingLabel = `${awaitingCount} ${awaitingCount === 1 ? 'task' : 'tasks'} waiting on you`

  return (
    <header className="relative flex items-center justify-between px-6 py-4 bg-cosmic-depth border-b border-cosmic-border max-lg:px-4 max-lg:py-3">
      <div className="flex items-center gap-4 max-lg:gap-2 min-w-0">
        <div className="flex items-center gap-2 flex-shrink-0">
          <img src={isCorporate ? sidewayLogo : sfLogo} alt="Star Forge" className="w-12 h-12 max-lg:w-8 max-lg:h-8 rounded-lg object-contain" />
          <span className={`font-semibold text-lg max-lg:hidden ${isCorporate ? 'text-slate-800' : 'text-indigo-400'}`}>Star Forge</span>
        </div>
        <div className="h-6 w-px bg-cosmic-border max-lg:hidden" />
        <h1 className="text-lg font-medium text-gray-200 truncate max-lg:text-sm">{projectName}</h1>
        <div className="h-6 w-px bg-cosmic-border max-lg:hidden" />
        {showAwaiting && (
          <button
            data-testid="awaiting-badge"
            onClick={onAwaitingClick}
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-colors"
            aria-label={awaitingLabel}
          >
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-amber-400 motion-safe:animate-pulse" />
            <span>
              {awaitingCount} {awaitingCount === 1 ? 'task' : 'tasks'} waiting on you
            </span>
            <span aria-hidden="true">→</span>
          </button>
        )}
        {showAwaiting && (
          <button
            data-testid="awaiting-badge-mobile"
            onClick={onAwaitingClick}
            className="sm:hidden inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border border-amber-500/30 bg-amber-500/10 text-amber-300"
            aria-label={awaitingLabel}
          >
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-amber-400 motion-safe:animate-pulse" />
            <span>{awaitingCount}</span>
          </button>
        )}
        <div className="max-lg:hidden">
          <ProjectSelector key={projectListKey} currentName={currentProjectName} onProjectChange={onProjectChange ? (project) => onProjectChange(project.name, project.workflow_name) : undefined} getAuthHeaders={getAuthHeaders} />
        </div>
      </div>

      {/* Desktop actions */}
      <div className="flex items-center gap-4 max-lg:hidden">
        {/* New Project button */}
        {onNewProject && (
          <button
            onClick={onNewProject}
            className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors"
          >
            New Project
          </button>
        )}

        {/* Stop button: names its project and asks first */}
        {onStopProject && (stopConfirmation || (
          <button
            onClick={requestStop}
            title={stopWorkflowId ? `Stop ${stopTargetName(stopWorkflowId)}` : undefined}
            className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors"
          >
            Stop
          </button>
        ))}

        {/* Resume button (shown when project is stopped) */}
        {onResumeProject && !onStopProject && (
          <div className="flex items-center gap-2">
            {showAmendConfirm ? (
              <div className="flex items-center gap-2 bg-amber-900/30 border border-amber-600/50 rounded-lg px-3 py-2">
                <span className="text-sm text-amber-200">This will start a new brainstorm session. Continue?</span>
                <button
                  onClick={confirmAmendResume}
                  className="px-3 py-1 text-sm font-medium text-white bg-amber-600 hover:bg-amber-500 rounded transition-colors"
                >
                  Yes
                </button>
                <button
                  onClick={() => setShowAmendConfirm(false)}
                  className="px-3 py-1 text-sm font-medium text-gray-300 hover:text-white bg-cosmic-surface rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : showResumeInput ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={resumeMessage}
                  onChange={(e) => setResumeMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleResumeWithIntent()}
                  placeholder="New intent"
                  className="px-3 py-1.5 text-sm bg-cosmic-surface border border-cosmic-border rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent w-64"
                  autoFocus
                />
                <button
                  onClick={handleResumeWithIntent}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-500 rounded-lg transition-colors"
                >
                  Go
                </button>
                <button
                  onClick={() => { setShowResumeInput(false); setResumeMessage(''); }}
                  className="p-1.5 text-gray-400 hover:text-white rounded transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <button
                  onClick={handlePlainResume}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-500 rounded-lg transition-colors"
                >
                  Resume
                </button>
                <button
                  onClick={() => setShowResumeInput(true)}
                  className="p-1.5 text-gray-400 hover:text-emerald-400 rounded transition-colors"
                  title="Resume with a new intent"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        {/* WebSocket Status */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-400">WebSocket:</span>
          <div
            data-testid="ws-status"
            className={`w-2 h-2 rounded-full ${statusColors[wsStatus]} ${
              wsStatus === 'connecting' ? 'animate-pulse' : ''
            }`}
            title={statusLabels[wsStatus]}
          />
          <span className="text-gray-300">{statusLabels[wsStatus]}</span>
        </div>

        {/* Settings Button */}
        {onSettingsClick && (
          <button
            onClick={onSettingsClick}
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
            aria-label="Open settings"
          >
            <svg className="w-5 h-5 text-gray-400 hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        )}
      </div>

      {/* Mobile: hamburger + status dot */}
      <div className="hidden max-lg:flex items-center gap-3">
        <div
          className={`w-2 h-2 rounded-full ${statusColors[wsStatus]} ${
            wsStatus === 'connecting' ? 'animate-pulse' : ''
          }`}
          title={statusLabels[wsStatus]}
        />
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 hover:bg-white/5 rounded-lg transition-colors"
          aria-label="Toggle menu"
        >
          <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="hidden max-lg:block absolute top-full left-0 right-0 z-50 bg-cosmic-depth border-t border-cosmic-border px-4 py-3 space-y-3">
          <div className="flex items-center gap-2 text-sm text-gray-300">
            <div className={`w-2 h-2 rounded-full ${statusColors[wsStatus]}`} />
            <span>WebSocket: {statusLabels[wsStatus]}</span>
          </div>

          <div>
            <ProjectSelector key={projectListKey} currentName={currentProjectName} onProjectChange={onProjectChange ? (project) => onProjectChange(project.name, project.workflow_name) : undefined} getAuthHeaders={getAuthHeaders} />
          </div>

          <div className="flex flex-col gap-2">
            {onNewProject && (
              <button
                onClick={() => { onNewProject(); setMobileMenuOpen(false); }}
                className="w-full px-4 py-3 text-base font-medium text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors"
              >
                New Project
              </button>
            )}
            {onStopProject && (stopConfirmation || (
              <button
                onClick={requestStop}
                className="w-full px-4 py-3 text-base font-medium text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors"
              >
                Stop Project
              </button>
            ))}
            {onResumeProject && !onStopProject && (
              <div className="space-y-2">
                {showAmendConfirm ? (
                  <div className="bg-amber-900/30 border border-amber-600/50 rounded-lg p-3 space-y-2">
                    <p className="text-sm text-amber-200">This will start a new brainstorm session. Continue?</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { confirmAmendResume(); setMobileMenuOpen(false); }}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-500 rounded-lg transition-colors"
                      >
                        Yes, brainstorm
                      </button>
                      <button
                        onClick={() => setShowAmendConfirm(false)}
                        className="flex-1 px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-cosmic-surface rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { handlePlainResume(); setMobileMenuOpen(false); }}
                        className="flex-1 px-4 py-3 text-base font-medium text-white bg-emerald-600 hover:bg-emerald-500 rounded-lg transition-colors"
                      >
                        Resume Project
                      </button>
                      <button
                        onClick={() => setShowResumeInput(!showResumeInput)}
                        className="px-3 py-3 text-gray-400 hover:text-emerald-400 bg-cosmic-surface rounded-lg transition-colors"
                        title="Resume with a new intent"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    </div>
                    {showResumeInput && (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={resumeMessage}
                          onChange={(e) => setResumeMessage(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleResumeWithIntent() }}
                          placeholder="New intent"
                          className="flex-1 px-3 py-2 text-sm bg-cosmic-surface border border-cosmic-border rounded-lg text-gray-200 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          autoFocus
                        />
                        <button
                          onClick={handleResumeWithIntent}
                          className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-500 rounded-lg transition-colors"
                        >
                          Go
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {onSettingsClick && (
              <button
                onClick={() => { onSettingsClick(); setMobileMenuOpen(false); }}
                className="w-full px-4 py-3 text-base font-medium text-gray-300 hover:text-white bg-cosmic-surface hover:bg-cosmic-surface/80 rounded-lg transition-colors"
              >
                Settings
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  )
}
